import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {MxFormErrorMessageService} from '../../../../services/mx-form-error-message.service';
import {MxField} from '../../services/mxField';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from "../../../../../../shared/services/mx-translate.service";
import {Subscription} from 'rxjs';
import DOMPurify from 'dompurify';

let isDomPurifyInitialized = false;

@Component({
    selector: 'mx-html-field',
    standalone: false,
    templateUrl: './mx-html-field.component.html',
    styleUrls: ['./mx-html-field.component.scss']
})
export class MxHtmlFieldComponent extends MxField implements OnDestroy, OnInit {
    public editorConfig: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: '10rem',
        minHeight: '5rem',
        sanitize: false,
        placeholder: 'Enter text here...',
        outline: false,
        translate: 'no'
    };

    subscription?: Subscription;

    constructor(
        protected errorService: MxFormErrorMessageService,
        @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService
    ) {
        super(errorService, translateService);
    }


    ngOnInit(): void {
        if (!isDomPurifyInitialized) {
            DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
                if (data.attrName?.startsWith('on')) {
                    data.keepAttr = false;
                }
                if (data.attrName === 'http-equiv' && data.attrValue?.trim().toLowerCase() === 'refresh') {
                    data.keepAttr = false;
                }
            });

            DOMPurify.addHook('afterSanitizeAttributes', node => {
                if (
                    node.tagName === 'A' &&
                    node.getAttribute('target') === '_blank'
                ) {
                    node.setAttribute('rel', 'noopener noreferrer');
                }
            });

            isDomPurifyInitialized = true;
        }

        const control = this.form.controls[this.name];

        this.subscription = control.valueChanges.subscribe(value => {
            if (!value) {
                return;
            }
            if (this.hasMalformedHtmlLikeInput(value)) {
                return;
            }

            if (!this.shouldSanitize(value)) {
                return;
            }

            const clean = this.sanitizeHtml(value);

            if (clean === value) {
                return;
            }

            setTimeout(() => {
                control.setValue(clean, {
                    emitEvent: false,
                    emitModelToViewChange: false
                });
            }, 50);
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private hasMalformedHtmlLikeInput(value: string): boolean {
        let insideTag = false;
        let quote: '"' | "'" | null = null;

        for (let i = 0; i < value.length; i++) {
            const ch = value[i];
            if (!insideTag) {
                if (ch === '<') {
                    insideTag = true;
                }
                continue;
            }

            if (quote) {
                if (ch === quote) {
                    quote = null;
                }
                continue;
            }

            if (ch === '"' || ch === '\'') {
                quote = ch;
                continue;
            }

            if (ch === '<') {
                return true;
            }

            if (ch === '>') {
                insideTag = false;
            }
        }

        return insideTag || quote !== null;
    }

    private shouldSanitize(value: string): boolean {
        return /<script\b|<svg\b|<math\b|<img\b|on\w+\s*=|javascript\s*:|data\s*:text\/html/i
            .test(value);
    }

    private sanitizeHtml(html: string): string {
        DOMPurify.clearConfig();

        const comments: string[] = [];

        const htmlWithPlaceholders = html.replace(
            /<!--[\s\S]*?-->/g,
            match => {
                const index = comments.push(match) - 1;

                return `@@MX_COMMENT_${index}@@`;
            }
        );

        const config = {
            ALLOWED_TAGS: [
                'a', 'b', 'br', 'body', 'div', 'em', 'hr', 'i',
                'li', 'ol', 'p', 'span', 'strong', 'u', 'ul',
                'img',
                'table', 'tbody', 'thead', 'tfoot', 'tr', 'td', 'th',
                'html', 'head', 'h1', 'h2', 'h3',
                'meta', 'title'
            ],
            ALLOWED_ATTR: [
                'alt', 'href', 'src', 'target', 'title', 'style', 'class', 'charset',
                'width', 'height',
                'align', 'valign',
                'cellpadding', 'cellspacing', 'border',
                'colspan', 'rowspan',
                'bgcolor',
                'http-equiv', 'content', 'name'
            ],
            ADD_URI_SAFE_ATTR: [
                'charset', 'content', 'http-equiv',
                'width', 'height',
                'align', 'valign',
                'cellpadding', 'cellspacing', 'border',
                'colspan', 'rowspan',
                'bgcolor', 'target'
            ],
            ALLOW_DATA_ATTR: false,
            ALLOW_UNKNOWN_PROTOCOLS: false,
            ALLOWED_URI_REGEXP:
                /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpeg|jpg|gif|webp);base64,|#)/i,
            FORBID_TAGS: ['math', 'script', 'svg'],
            FORCE_BODY: false,
            KEEP_CONTENT: true,
            SANITIZE_DOM: true,
            WHOLE_DOCUMENT: true
        };

        DOMPurify.removed = [];

        const clean = DOMPurify.sanitize(htmlWithPlaceholders, config);

        if (DOMPurify.removed.length === 0) {
            return html;
        }

        return clean.replace(
            /@@MX_COMMENT_(\d+)@@/g,
            (_match, index) => comments[Number(index)] ?? ''
        );
    }
}